import { isStrayCypherExpr } from './stray'

/**
 * Detail bundle for a single `:Line` OR `:Subline` (`$lineId` param).
 * Sublines are also `:Line` nodes attached via `HAS_SUBLINE`, so
 * this query handles both cases — the orientation just differs in
 * which side of the `parentLine` / `top` walk binds.
 *
 * Returns:
 *   - `l`                  — the line node itself
 *   - `parentLine`         — the immediate `:Line` parent if this
 *                            line is a subline, else null
 *   - `site`               — the owning `:Site` (resolved by walking
 *                            up `HAS_SUBLINE*` to a `top` line that
 *                            is NOT a subline, then following
 *                            `Site -[:HAS_LINE]-> top`)
 *   - `process`            — the `:Process` this line `INSTANTIATES`
 *                            (collapsed with `head(collect(…))` when
 *                            multiple edges exist — schema says one)
 *   - `directEquipment`    — every `:Equipment` directly attached to
 *                            this line (children of sublines are
 *                            covered by the per-subline summaries
 *                            below)
 *   - `sublineSummaries`   — same shape as Site detail's
 *                            `lineSummaries`, but for the direct
 *                            `HAS_SUBLINE` children of this line
 *                            (also collapses multi-process fan-out)
 *   - `controller`         — the first `:Controller` this line is
 *                            `CONTROLLED_BY` (lines typically have a
 *                            single PLC controller assigned)
 *   - `computes`           — every `:Compute` reached via either the
 *                            line itself OR any equipment in any of
 *                            its sublines via `MONITORED_BY`
 *   - `utilities`          — Utility services owned directly by this
 *                            line via `HAS_UTILITY`
 *   - `sublineFeeds`       — directed `Line -[:FEEDS]-> Line` edges
 *                            between this line's direct sublines,
 *                            for the per-line connectivity diagram
 *   - `equipmentConnects`  — directed `Equipment -[:CONNECTS_TO]->
 *                            Equipment` edges among this line's
 *                            direct equipment (physical connectivity
 *                            diagram; distinct from WIP FEEDS)
 *   - `procedures`         — inbound `Procedure -[:PROCEDURE_FOR]-> Line`
 *
 * Bounded by `LIMIT 10000`.
 */
export const PLANT_LINE_DETAIL_CYPHER = `
            MATCH (l:Line {id: $lineId})
            OPTIONAL MATCH (parentLine:Line)-[:HAS_SUBLINE]->(l)
            OPTIONAL MATCH (top:Line)-[:HAS_SUBLINE*0..]->(l)
              WHERE NOT (:Line)-[:HAS_SUBLINE]->(top)
            OPTIONAL MATCH (site:Site)-[:HAS_LINE]->(top)
            OPTIONAL MATCH (l)-[:INSTANTIATES]->(p:Process)
            WITH l, parentLine, site, head(collect(distinct p)) AS process
            OPTIONAL MATCH (l)-[:HAS_EQUIPMENT]->(directEq:Equipment)
            WITH l, parentLine, site, process, collect(distinct directEq) AS directEquipment
            OPTIONAL MATCH (l)-[:HAS_SUBLINE]->(sub:Line)
            WITH l, parentLine, site, process, directEquipment,
                 collect(distinct sub) AS sublines
            UNWIND (CASE WHEN size(sublines) = 0 THEN [null] ELSE sublines END) AS subNode
            OPTIONAL MATCH (subNode)-[:INSTANTIATES]->(subP:Process)
            WITH l, parentLine, site, process, directEquipment, sublines, subNode,
                 head(collect(distinct subP)) AS subProcess
            OPTIONAL MATCH (subNode)-[:HAS_SUBLINE]->(grandchild:Line)
            WITH l, parentLine, site, process, directEquipment, sublines, subNode, subProcess,
                 count(distinct grandchild) AS subSublineCount
            OPTIONAL MATCH (subNode)-[:HAS_SUBLINE*0..]->(scope:Line)
            OPTIONAL MATCH (scope)-[:HAS_EQUIPMENT]->(scopeEq:Equipment)
            WITH l, parentLine, site, process, directEquipment, sublines, subNode, subProcess,
                 subSublineCount, count(distinct scopeEq) AS subEquipmentCount,
                 [eqId IN collect(distinct scopeEq.id) WHERE eqId IS NOT NULL] AS subEquipmentIds
            WITH l, parentLine, site, process, directEquipment, sublines,
                 collect(distinct {
                   line: subNode,
                   process: subProcess,
                   sublineCount: subSublineCount,
                   equipmentCount: subEquipmentCount,
                   equipmentIds: subEquipmentIds
                 }) AS sublineSummaries
            OPTIONAL MATCH (l)-[:CONTROLLED_BY]->(controller:Controller)
            WITH l, parentLine, site, process, directEquipment, sublines, sublineSummaries,
                 head(collect(distinct controller)) AS controller
            OPTIONAL MATCH (l)-[:MONITORED_BY]->(mc:Compute)
            OPTIONAL MATCH (l)-[:HAS_SUBLINE*0..]->(covLine:Line)-[:HAS_EQUIPMENT]->(covEq:Equipment)-[:MONITORED_BY]->(eqc:Compute)
            WITH l, parentLine, site, process, directEquipment, sublines, sublineSummaries,
                 controller, collect(distinct mc) + collect(distinct eqc) AS computes
            OPTIONAL MATCH (l)-[:HAS_UTILITY]->(utility:Utility)
            WITH l, parentLine, site, process, directEquipment, sublines, sublineSummaries,
                 controller, computes,
                 [n IN collect(DISTINCT utility) WHERE n IS NOT NULL] AS utilities
            OPTIONAL MATCH (proc:Procedure)-[:PROCEDURE_FOR]->(l)
            WITH l, parentLine, site, process, directEquipment, sublines, sublineSummaries,
                 controller, computes, utilities,
                 [n IN collect(DISTINCT proc) WHERE n IS NOT NULL] AS procedures
            OPTIONAL MATCH (a:Line)-[f:FEEDS]->(b:Line)
              WHERE a IN sublines AND b IN sublines
            OPTIONAL MATCH (eqA:Equipment)-[ct:CONNECTS_TO]->(eqB:Equipment)
              WHERE eqA IN directEquipment AND eqB IN directEquipment
            RETURN l,
                   parentLine,
                   site,
                   process,
                   directEquipment,
                   sublineSummaries,
                   controller,
                   computes,
                   utilities,
                   procedures,
                   collect(distinct {
                     type: type(f),
                     source: a.id,
                     target: b.id,
                     props: properties(f)
                   }) AS sublineFeeds,
                   [edge IN collect(distinct {
                     type: type(ct),
                     source: eqA.id,
                     target: eqB.id,
                     props: properties(ct)
                   }) WHERE edge.source IS NOT NULL] AS equipmentConnects,
                   ${isStrayCypherExpr('l', 'Line')} AS isStray
            LIMIT 10000
          `
